import { UserAdminFilters } from './components/UserAdminFilters';
import { UserColumns } from './components/UserColumns';
import {
	useGetUsers,
	useGetClubs,
	useGetCompanies,
	usePatchUserBanned,
} from '@/services';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useModal } from '@/hooks';
import {
	AlertContainer,
	DataTable,
	PageTitle,
	SearchInput,
} from '@/components';
import { getBackendErrorMessage } from '@/utils/errorMessage';

const Users = () => {
	const { openModal, setOnConfirm, setConfirmText, setCancelText } =
		useModal();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);

	const { data: clubsData } = useGetClubs();
	const clubs = Array.isArray(clubsData) ? clubsData : clubsData?.data ?? [];

	const { data: companiesResponse } = useGetCompanies(
		{ active: true },
		{ staleTime: 5 * 60 * 1000 },
	);
	const companies = Array.isArray(companiesResponse)
		? companiesResponse
		: companiesResponse?.data ?? [];

	const bannedParams = searchParams.get('banned') as 'true' | 'false';
	const isCompleteParams = searchParams.get('is_complete') as
		| 'true'
		| 'false';
	const clubIdParams = searchParams.get('club_id') || undefined;
	const companyIdParams = searchParams.get('company_id') || undefined;
	const search = searchParams.get('search');

	const {
		data: usersData,
		error,
		isError,
	} = useGetUsers({
		page,
		limit,
		...(bannedParams && { banned: bannedParams }),
		...(isCompleteParams && { is_complete: isCompleteParams }),
		...(clubIdParams && { club_id: clubIdParams }),
		...(companyIdParams && { company_id: companyIdParams }),
		...(search && { search }),
	});
	const totalUsers =
		usersData?.pagination?.total ??
		usersData?.total ??
		usersData?.data?.length ??
		0;

	const { mutate: mutateUserBanned } = usePatchUserBanned();

	const handlePageChange = (newPage: number) => setPage(newPage);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	useEffect(() => {
		if (page !== 1) handlePageChange(1);
	}, [bannedParams, isCompleteParams, clubIdParams, companyIdParams, search]);

	const onEdit = (id: string) => {
		navigate(`/admin/pacientes/editar/${id}`);
	};

	const onToggleStatus = (ban: boolean, username: string, id: string) => {
		setConfirmText(ban ? 'Confirmar desativação' : 'Confirmar ativação');
		setCancelText('Cancelar');

		const action = ban ? 'desativar' : 'ativar';
		const message = (
			<span>
				{`Tem certeza que deseja ${action} o usuário `}
				<span className="text-base font-semibold text-red-500">
					{username}
				</span>
			</span>
		);
		openModal({
			content: <AlertContainer message={message} />,
		});
		setOnConfirm(() => mutateUserBanned({ id, body: { ban } }));
	};

	const searchErrorMessage =
		search?.trim() && isError ? getBackendErrorMessage(error) : null;

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="space-y-1">
				<PageTitle text="Pacientes" />
				<p className="text-sm text-stone-400">
					Gerencie os pacientes cadastrados na plataforma.
				</p>
			</div>
			<div className="space-y-2">
				<SearchInput
					placeholder="Pesquisar por nome..."
					className="max-w-full bg-inherit md:max-w-full"
				/>
				{searchErrorMessage && (
					<p className="text-sm text-red-600" role="alert">
						{searchErrorMessage}
					</p>
				)}
			</div>
			<UserAdminFilters clubs={clubs} companies={companies} />
			{usersData && (
				<DataTable
					tableClassName="table-fixed w-full"
					columns={UserColumns({
						onEdit,
						onToggleStatus,
						clubs,
					})}
					data={usersData?.data ?? []}
					page={page}
					limit={limit}
					totalItems={totalUsers}
					onPageChange={handlePageChange}
					onLimitChange={handleLimitChange}
				/>
			)}
		</section>
	);
};

export { Users };
